import { QuoteState, QuoteAttribute, AttributeSelection, AttributeOption } from '@/schemas/quoteResponseSchema';
import { useQuoteStore } from './use-quote-store';
import { PricingTerm } from '@/constants/pricingTypes';

// ============================================================================
// PENDING REQUEST MANAGEMENT
// ============================================================================

export const UPDATE_DEBOUNCE_MS = 300;

interface PendingRequest {
  timeout: ReturnType<typeof setTimeout> | null;
  controller: AbortController | null;
}

const pendingRequest: PendingRequest = {
  timeout: null,
  controller: null,
};

export const cancelPendingRequests = () => {
  if (pendingRequest.timeout) clearTimeout(pendingRequest.timeout);
  if (pendingRequest.controller) pendingRequest.controller.abort();
  pendingRequest.timeout = null;
  pendingRequest.controller = null;
};

export const setPendingTimeout = (callback: () => void, delay: number) => {
  pendingRequest.timeout = setTimeout(() => {
    pendingRequest.timeout = null;
    callback();
  }, delay);
};

export const setPendingController = (controller: AbortController) => {
  pendingRequest.controller = controller;
};

export const getPendingController = (): AbortController | null => {
  return pendingRequest.controller;
};

export const clearPendingController = () => {
  pendingRequest.controller = null;
};

export const hasInFlightRequests = (): boolean => {
  return pendingRequest.controller !== null;
};

export const hasPendingRequests = (): boolean => {
  return pendingRequest.timeout !== null || pendingRequest.controller !== null;
};

export const waitForPendingRequests = (timeoutMs = 5000): Promise<void> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const checkPending = () => {
      if (!hasPendingRequests()) {
        resolve();
      } else if (Date.now() - startTime > timeoutMs) {
        reject(new Error('Timeout waiting for pending requests'));
      } else {
        setTimeout(checkPending, 100);
      }
    };
    checkPending();
  });
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

export interface ValidationError {
  name: string;
  message: string;
}

export const getValidationErrors = (quote: QuoteState): ValidationError[] => {
  return quote.coverages
    .filter((c) => c.validation && !c.validation.valid)
    .map((c) => ({
      name: c.name ?? `${c.symbol} (${c.scope})`,
      message: c.validation?.message ?? 'Please review this coverage',
    }))
    .filter((c, i, arr) => arr.findIndex((x) => x.name === c.name && x.message === c.message) === i);
};

export const hasValidationErrors = (quote: QuoteState): boolean => {
  return quote.coverages.some((c) => c.validation && !c.validation.valid);
};

// ============================================================================
// SELECTION MATCHING
// ============================================================================

export const selectionsMatch = (a: AttributeSelection, b: AttributeSelection): boolean => {
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);

  for (const key of allKeys) {
    const aVal = (a as Record<string, unknown>)[key];
    const bVal = (b as Record<string, unknown>)[key];
    if (aVal == null && bVal == null) continue;
    if (aVal == null || bVal == null) return false;
    if (aVal !== bVal) return false;
  }
  return true;
};

export const findMatchingOption = (
  selection: AttributeSelection | null | undefined,
  options: AttributeOption[]
): AttributeOption | undefined => {
  if (!selection) return undefined;
  return options.find((opt) => selectionsMatch(opt.value, selection));
};

// ============================================================================
// PRICE DELTA HELPERS
// ============================================================================

export const hasPriceDeltaForSelection = (
  attribute: QuoteAttribute | undefined,
  selection: AttributeSelection
): boolean => {
  if (!attribute?.priceDeltas.length) return false;

  const matchingOption = findMatchingOption(selection, attribute.selectionOptions);
  if (!matchingOption) return false;

  return attribute.priceDeltas.some((delta) => delta.associatedSelection === matchingOption.label);
};

export const applyPriceDeltaToQuote = (attribute: QuoteAttribute | undefined, selection: AttributeSelection): void => {
  if (!attribute?.priceDeltas.length) return;

  const matchingOption = findMatchingOption(selection, attribute.selectionOptions);
  if (!matchingOption) return;

  const delta = attribute.priceDeltas.find((d) => d.associatedSelection === matchingOption.label);
  if (!delta) return;

  const { quote, setQuote } = useQuoteStore.getState();
  if (!quote) return;

  const isMonthly = quote.details.initial_term_selection === PricingTerm.MONTHLY;
  const deltaInCents = delta.amountInCents;
  const deltaInDollars = deltaInCents / 100;

  setQuote({
    ...quote,
    details: {
      ...quote.details,
      ...(isMonthly
        ? {
            monthly_in_cents: quote.details.monthly_in_cents + deltaInCents,
            due_today_monthly_in_dollars: quote.details.due_today_monthly_in_dollars + deltaInDollars,
          }
        : {
            full_term_in_cents: quote.details.full_term_in_cents + deltaInCents,
            due_today_full_term_in_dollars: quote.details.due_today_full_term_in_dollars + deltaInDollars,
          }),
    },
  });
};

// ============================================================================
// API RESPONSE MERGING
// ============================================================================

/** True when the response contains real (non-zero) pricing data. */
export const hasRealPrices = (details: QuoteState['details']): boolean => {
  return details.monthly_in_cents > 0 || details.full_term_in_cents > 0;
};

/** Determine whether the current store prices should be kept over the API response.
 *  True when there are validation errors OR the response has no real prices. */
export const shouldPreservePrices = (hasErrors: boolean, responseDetails: QuoteState['details']): boolean => {
  return hasErrors || !hasRealPrices(responseDetails);
};

export const mergeApiResponse = (response: QuoteState, preservePrices = false) => {
  const setQuote = useQuoteStore.getState().setQuote;
  const currentState = useQuoteStore.getState().quote;
  const currentTerm = currentState?.details.initial_term_selection;

  setQuote({
    ...response,
    details: {
      ...response.details,
      initial_term_selection: currentTerm ?? response.details.initial_term_selection,
      ...(preservePrices && currentState
        ? {
            monthly_in_cents: currentState.details.monthly_in_cents,
            full_term_in_cents: currentState.details.full_term_in_cents,
            full_term_savings_in_cents: currentState.details.full_term_savings_in_cents,
            due_today_monthly_in_dollars: currentState.details.due_today_monthly_in_dollars,
            due_today_full_term_in_dollars: currentState.details.due_today_full_term_in_dollars,
          }
        : {}),
    },
  });
};
